// Structured names, addresses and phone numbers — shared by the server and both SPAs so
// the two sides can never disagree about what "normalised" means.

/** ISO-3166 alpha-2 → E.164 calling code. Covers where this practice actually works;
 *  an unlisted country still works if the operator types a +<code> prefix themselves. */
export const COUNTRIES: Array<{ code: string; name: string; dial: string }> = [
  { code: 'CH', name: 'Switzerland', dial: '41' },
  { code: 'GB', name: 'United Kingdom', dial: '44' },
  { code: 'DE', name: 'Germany', dial: '49' },
  { code: 'FR', name: 'France', dial: '33' },
  { code: 'IT', name: 'Italy', dial: '39' },
  { code: 'AT', name: 'Austria', dial: '43' },
  { code: 'LI', name: 'Liechtenstein', dial: '423' },
  { code: 'NL', name: 'Netherlands', dial: '31' },
  { code: 'BE', name: 'Belgium', dial: '32' },
  { code: 'LU', name: 'Luxembourg', dial: '352' },
  { code: 'IE', name: 'Ireland', dial: '353' },
  { code: 'ES', name: 'Spain', dial: '34' },
  { code: 'PT', name: 'Portugal', dial: '351' },
  { code: 'DK', name: 'Denmark', dial: '45' },
  { code: 'SE', name: 'Sweden', dial: '46' },
  { code: 'NO', name: 'Norway', dial: '47' },
  { code: 'FI', name: 'Finland', dial: '358' },
  { code: 'IS', name: 'Iceland', dial: '354' },
  { code: 'PL', name: 'Poland', dial: '48' },
  { code: 'CZ', name: 'Czechia', dial: '420' },
  { code: 'SK', name: 'Slovakia', dial: '421' },
  { code: 'HU', name: 'Hungary', dial: '36' },
  { code: 'RO', name: 'Romania', dial: '40' },
  { code: 'BG', name: 'Bulgaria', dial: '359' },
  { code: 'GR', name: 'Greece', dial: '30' },
  { code: 'HR', name: 'Croatia', dial: '385' },
  { code: 'SI', name: 'Slovenia', dial: '386' },
  { code: 'RS', name: 'Serbia', dial: '381' },
  { code: 'EE', name: 'Estonia', dial: '372' },
  { code: 'LV', name: 'Latvia', dial: '371' },
  { code: 'LT', name: 'Lithuania', dial: '370' },
  { code: 'UA', name: 'Ukraine', dial: '380' },
  { code: 'TR', name: 'Türkiye', dial: '90' },
  { code: 'US', name: 'United States', dial: '1' },
  { code: 'CA', name: 'Canada', dial: '1' },
  { code: 'MX', name: 'Mexico', dial: '52' },
  { code: 'BR', name: 'Brazil', dial: '55' },
  { code: 'AR', name: 'Argentina', dial: '54' },
  { code: 'CL', name: 'Chile', dial: '56' },
  { code: 'ZA', name: 'South Africa', dial: '27' },
  { code: 'NG', name: 'Nigeria', dial: '234' },
  { code: 'KE', name: 'Kenya', dial: '254' },
  { code: 'GH', name: 'Ghana', dial: '233' },
  { code: 'EG', name: 'Egypt', dial: '20' },
  { code: 'MA', name: 'Morocco', dial: '212' },
  { code: 'AE', name: 'United Arab Emirates', dial: '971' },
  { code: 'SA', name: 'Saudi Arabia', dial: '966' },
  { code: 'IL', name: 'Israel', dial: '972' },
  { code: 'IN', name: 'India', dial: '91' },
  { code: 'PK', name: 'Pakistan', dial: '92' },
  { code: 'SG', name: 'Singapore', dial: '65' },
  { code: 'HK', name: 'Hong Kong SAR', dial: '852' },
  { code: 'CN', name: 'China', dial: '86' },
  { code: 'JP', name: 'Japan', dial: '81' },
  { code: 'KR', name: 'South Korea', dial: '82' },
  { code: 'AU', name: 'Australia', dial: '61' },
  { code: 'NZ', name: 'New Zealand', dial: '64' },
];

const BY_CODE = new Map(COUNTRIES.map((c) => [c.code, c]));

export const countryName = (code?: string | null): string =>
  (code && BY_CODE.get(code)?.name) || code || '';

// ── Names ───────────────────────────────────────────────────────────────────

/** Display name from the structured parts, falling back to whatever exists. */
export function composeName(first?: string | null, last?: string | null, fallback?: string | null): string {
  const composed = [first, last].map((p) => (p ?? '').trim()).filter(Boolean).join(' ');
  return composed || (fallback ?? '').trim();
}

/** Best-effort split of a legacy single-field name. Last token is the family name;
 *  everything before it is given names. Particles (van, von, de …) stay with the family
 *  name, which is the common European case this practice actually meets. */
export function splitName(full: string): { first: string; last: string } {
  const parts = (full ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: '', last: '' };
  if (parts.length === 1) return { first: parts[0], last: '' };

  const PARTICLES = new Set(['van', 'von', 'de', 'del', 'della', 'di', 'da', 'du', 'der', 'den', 'ter', 'la', 'le', 'bin', 'al']);
  let cut = parts.length - 1;
  while (cut > 1 && PARTICLES.has(parts[cut - 1].toLowerCase())) cut--;
  return { first: parts.slice(0, cut).join(' '), last: parts.slice(cut).join(' ') };
}

// ── Addresses ───────────────────────────────────────────────────────────────

export interface StructuredAddress {
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  postcode?: string | null;
  country?: string | null;   // ISO alpha-2
}

/** The lines of a postal address, in the order they go on an envelope. */
export function addressLines(a: StructuredAddress, legalName?: string | null): string[] {
  const cityLine = [a.postcode, a.city].map((p) => (p ?? '').trim()).filter(Boolean).join(' ');
  return [
    (legalName ?? '').trim(),
    (a.address_line1 ?? '').trim(),
    (a.address_line2 ?? '').trim(),
    cityLine,
    countryName(a.country),
  ].filter(Boolean);
}

/** Single-line form, for the legacy `clients.address` column and compact UI. */
export const formatAddress = (a: StructuredAddress, legalName?: string | null): string =>
  addressLines(a, legalName).join(', ');

// ── Phone numbers ───────────────────────────────────────────────────────────

export interface PhoneResult {
  /** Strict E.164 (+ then 7–15 digits) or null when it cannot be understood. */
  e164: string | null;
  /** Grouped for reading; falls back to the raw input when unparseable. */
  display: string;
  valid: boolean;
  reason?: string;
}

/**
 * Normalise a typed phone number to E.164.
 *
 * Handles what people actually type: "+41 (0)79 411 55 35", "0041 79 411 55 35",
 * "079 411 55 35" with a country, dots, dashes and non-breaking spaces. The "(0)"
 * national trunk prefix is dropped after a country code — writing it is a common
 * convention and keeping it produces a number that cannot be dialled internationally.
 */
export function normalisePhone(raw: string | null | undefined, countryCode?: string | null): PhoneResult {
  const input = (raw ?? '').replace(/[   ]/g, ' ').trim();
  if (!input) return { e164: null, display: '', valid: true };

  // Drop a parenthesised trunk zero anywhere: "+41 (0)79…" → "+41 79…"
  let s = input.replace(/\((0+)\)/g, '');
  const hadPlus = s.trimStart().startsWith('+') || /^\s*00\d/.test(s);
  s = s.replace(/^\s*00/, '+');
  let digits = s.replace(/[^\d+]/g, '');
  digits = digits.startsWith('+') ? '+' + digits.slice(1).replace(/\+/g, '') : digits.replace(/\+/g, '');

  if (!hadPlus && !digits.startsWith('+')) {
    const dial = countryCode ? BY_CODE.get(countryCode)?.dial : undefined;
    if (!dial) {
      return {
        e164: null, display: input, valid: false,
        reason: 'Add the country code (for example +41), or set the country on the client first.',
      };
    }
    digits = '+' + dial + digits.replace(/^0+/, '');   // strip the national trunk zero
  }

  const e164 = digits.startsWith('+') ? digits : '+' + digits;
  if (!/^\+[1-9]\d{6,14}$/.test(e164)) {
    return {
      e164: null, display: input, valid: false,
      reason: 'That does not look like a phone number — expected 7 to 15 digits after the country code.',
    };
  }
  return { e164, display: formatPhone(e164), valid: true };
}

/** Group an E.164 number for reading: +41794115535 → +41 79 411 55 35. */
export function formatPhone(e164: string | null | undefined): string {
  if (!e164 || !e164.startsWith('+')) return e164 ?? '';
  const rest = e164.slice(1);
  const country = COUNTRIES.filter((c) => rest.startsWith(c.dial)).sort((a, b) => b.dial.length - a.dial.length)[0];
  if (!country) return e164;

  const national = rest.slice(country.dial.length);
  const groups =
    national.length === 9 ? [national.slice(0, 2), national.slice(2, 5), national.slice(5, 7), national.slice(7)]
    : national.length === 10 ? [national.slice(0, 4), national.slice(4, 7), national.slice(7)]
    : national.match(/.{1,3}/g) ?? [national];
  return `+${country.dial} ${groups.filter(Boolean).join(' ')}`;
}
